'use client'

/**
 * "Session recording attached" — the row the feedback modal draws for a
 * recording that has just been stopped (Issue #13788).
 *
 * ## Why a row at all
 *
 * Before this, stopping a recording left an outcome string in the banner and
 * nothing to do with it. `session-recording-context.tsx` nulled the active
 * session on stop and `feedback-modal.tsx` read that value at SUBMIT time, so
 * a modal opened after stopping attached nothing — a recording with a start, a
 * stop, a confirmation, and no way to send it anywhere. The slot in
 * `@/lib/feedback/active-session` is what makes the id survive; this is what
 * makes it VISIBLE, because an attachment nobody can see is indistinguishable
 * from the bug it replaces.
 *
 * ## Its own file, for a measured reason
 *
 * `feedback-modal.tsx` is 974 lines against `check:file-size-ratchet`'s
 * 1,000-line cap for a file with no baseline entry. A row that renders only
 * when a recording is pending has no business spending a quarter of the
 * remaining headroom, and `anchor-row.tsx` set this precedent for the same
 * file and the same reason.
 *
 * ## The remove control is a real button
 *
 * Removing the recording is the only way to send feedback about something else
 * without the session attached, so it carries an accessible name and the
 * repo's 44x44 floor rather than being a hover-revealed glyph.
 */

import { useCallback, useEffect, useState } from 'react'

import { Video, X } from '@/components/icons'
import {
  getPendingFeedbackSessionAttachment,
  setPendingFeedbackSessionAttachment,
  type PendingFeedbackSessionAttachment,
} from '@/lib/feedback/active-session'

/**
 * The modal's view of the pending recording, and the one way to clear it.
 *
 * Lives here rather than in `feedback-modal.tsx` for the file-size reason
 * above, and because the mirror and the row are one idea: the slot is a
 * mutable module variable React knows nothing about, so it is READ ONCE per
 * open into state rather than during render, where it would be a torn-render
 * bug waiting for its first concurrent update. The open transition is the only
 * moment it can change, and that transition is itself a React state change.
 */
export function usePendingSessionAttachment(isOpen: boolean): {
  pendingSession: PendingFeedbackSessionAttachment | null
  clearPendingSession: () => void
} {
  const [pendingSession, setPendingSession] = useState(
    getPendingFeedbackSessionAttachment,
  )
  useEffect(() => {
    if (isOpen) setPendingSession(getPendingFeedbackSessionAttachment())
  }, [isOpen])

  /**
   * Both halves, always: clearing only the React copy would leave the module
   * slot to re-attach the same recording to the next form the viewer opens.
   *
   * ## WHO calls it changed in #13841
   *
   * Three callers: the row's own remove control, and `feedback-modal.tsx`'s
   * close path — which used to run on EVERY close and now runs on submit only.
   *
   * #13788 cleared on dismissal as well, to stop a stopped-but-unused
   * recording silently attaching itself to some later, unrelated submission.
   * The cost was worse than the risk: the form a recording stop opens is
   * reached by pressing Stop, so backing out of it threw the recording away —
   * while the confirmation banner beside it still said the recording was kept,
   * offering a Dismiss for an attachment that no longer existed.
   *
   * That protection MOVED rather than being dropped. `dismissOutcome` in
   * `session-recording-context.tsx` clears the slot now, and it is the control
   * a viewer presses to say "forget this recording" — on screen for exactly as
   * long as there is a recording to forget.
   */
  const clearPendingSession = useCallback(() => {
    setPendingFeedbackSessionAttachment(null)
    setPendingSession(null)
  }, [])

  return { pendingSession, clearPendingSession }
}

/** `m:ss`. Minutes unpadded, matching the banner's clock. */
function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export function PendingSessionRow({
  attachment,
  onRemove,
}: {
  attachment: PendingFeedbackSessionAttachment
  onRemove: () => void
}) {
  const segments =
    attachment.chunks === 1 ? '1 segment' : `${attachment.chunks} segments`

  return (
    <div
      // Issue #13788 — a LITERAL string, never a constant. The golden-path
      // wiring check greps `src/` for the exact spelling a tagged spec
      // asserts, and an id emitted through an identifier scores zero there.
      data-testid="feedback-pending-session"
      className="flex items-center gap-2 rounded-md border border-stroke-regular bg-surface-panel px-3 py-1"
    >
      <Video
        className="w-4 h-4 shrink-0 text-text-secondary"
        aria-hidden="true"
      />
      <span className="shrink-0 text-sm text-text-secondary">
        Session recording:
      </span>
      <span className="truncate text-sm font-medium text-text-primary">
        {formatElapsed(attachment.seconds)} · {segments}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove session recording"
        className="ml-auto inline-flex size-8 pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px] shrink-0 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-fill-10 hover:text-text-secondary"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
